/*
  The product-media bar, usable on any scroller. Paste into Assets → scrollbar.js.
  One script tag in layout/theme.liquid. See scrollbar.md.

  This is the control that already lives on the product gallery: one segment
  per visible slide, the active segment marked is-active. It is not a
  proportional thumb.

  The bar element is the empty wrapper. The script fills it with the same
  buttons as assets/custom.js:

    <button class="slider-counter__link slider-counter__link--dots link">
      <span class="dot"></span>
    </button>

  PDP, no extra attributes, as long as the wrapper sits inside <media-gallery>
  and is either [data-media-dots] or .product-media-dots:
    viewport  .product__media-list
    slides    .product__media-item

  Product cards, same idea, inside [data-card-media-slider]:
    viewport  [data-card-media-viewport]
    slides    [data-card-media-slide]

  Anywhere else:

  data-scrollbar="horizontal" | "vertical"
    Axis when no mobile/desktop override applies. Omitted means horizontal.

  data-scrollbar-viewport=".your-scroller"
    The element that scrolls. Searched from this bar upward.

  data-scrollbar-slide=".your-slide"
    One segment per match inside the viewport. Omitted on a generic bar
    means the viewport’s direct children.

  data-scrollbar-mobile="horizontal" | "vertical"
    Optional. Axis below the breakpoint.

  data-scrollbar-desktop="horizontal" | "vertical"
    Optional. Axis at or above the breakpoint.

  data-scrollbar-breakpoint="750"
    Optional. Desktop starts at this width. Default 750 (Dawn: 0–749 mobile).

  The script sets data-scrollbar-axis="horizontal" | "vertical" on the bar.
  Color stays in the stylesheet that already paints .product-media-dots.
*/
(function () {
  'use strict';

  if (document.documentElement.hasAttribute('data-scrollbar-booted')) return;
  document.documentElement.setAttribute('data-scrollbar-booted', '');

  var SELECTOR = [
    '[data-scrollbar]',
    '[data-media-dots]',
    'media-gallery .product-media-dots',
    '[data-card-media-slider] .product-media-dots',
    '[data-card-media-dots]'
  ].join(', ');

  var DEFAULT_BREAKPOINT = 750;
  var HIDDEN_CLASS = 'is-hidden-by-color';

  function namedAxis(value) {
    if (value === 'horizontal' || value === 'vertical') return value;
    return null;
  }

  function breakpointOf(bar) {
    var value = parseInt(bar.getAttribute('data-scrollbar-breakpoint'), 10);
    if (!value || value < 1) return DEFAULT_BREAKPOINT;
    return value;
  }

  function axisFor(bar) {
    var mobile = window.matchMedia('(max-width: ' + (breakpointOf(bar) - 1) + 'px)').matches;
    var override = mobile
      ? namedAxis(bar.getAttribute('data-scrollbar-mobile'))
      : namedAxis(bar.getAttribute('data-scrollbar-desktop'));
    return override || namedAxis(bar.getAttribute('data-scrollbar')) || 'horizontal';
  }

  function findWithin(bar, selector) {
    if (!selector) return null;

    var node = bar;
    while (node) {
      if (node.matches && node.matches(selector)) return node;
      if (node.querySelector) {
        var found = node.querySelector(selector);
        if (found && found !== bar && !bar.contains(found)) return found;
      }
      node = node.parentElement;
    }

    return document.querySelector(selector);
  }

  function viewportFor(bar) {
    var chosen = findWithin(bar, bar.getAttribute('data-scrollbar-viewport'));
    if (chosen) return chosen;

    var gallery = bar.closest('media-gallery');
    if (gallery) {
      var productList = gallery.querySelector('.product__media-list');
      if (productList) return productList;
    }

    var card = bar.closest('[data-card-media-slider]');
    if (card) return card.querySelector('[data-card-media-viewport]');

    return null;
  }

  function slideNodes(bar, viewport) {
    var selector = bar.getAttribute('data-scrollbar-slide');
    if (selector) return viewport.querySelectorAll(selector);

    if (bar.closest('media-gallery')) return viewport.querySelectorAll('.product__media-item');
    if (bar.closest('[data-card-media-slider]')) return viewport.querySelectorAll('[data-card-media-slide]');

    return viewport.children;
  }

  function visibleSlides(bar, viewport) {
    return Array.prototype.filter.call(slideNodes(bar, viewport), function (slide) {
      if (!slide || slide.nodeType !== 1) return false;
      if (slide === bar || bar.contains(slide) || slide.contains(bar)) return false;
      if (slide.classList.contains(HIDDEN_CLASS)) return false;
      if (slide.hidden) return false;
      if (slide.offsetParent === null) return false;
      return true;
    });
  }

  function sameSlides(previous, next) {
    if (previous.length !== next.length) return false;
    for (var i = 0; i < previous.length; i++) {
      if (previous[i] !== next[i]) return false;
    }
    return true;
  }

  function activeIndex(viewport, slides, horizontal) {
    var box = viewport.getBoundingClientRect();
    var center = horizontal
      ? box.left + box.width / 2
      : box.top + box.height / 2;
    var best = 0;
    var smallest = Infinity;

    for (var i = 0; i < slides.length; i++) {
      var slideBox = slides[i].getBoundingClientRect();
      var slideCenter = horizontal
        ? slideBox.left + slideBox.width / 2
        : slideBox.top + slideBox.height / 2;
      var distance = Math.abs(center - slideCenter);
      if (distance < smallest) {
        smallest = distance;
        best = i;
      }
    }

    return best;
  }

  function scrollSlideIntoView(viewport, slide, horizontal) {
    var viewportBox = viewport.getBoundingClientRect();
    var slideBox = slide.getBoundingClientRect();

    if (horizontal) {
      viewport.scrollTo({
        left: viewport.scrollLeft + slideBox.left - viewportBox.left,
        behavior: 'smooth'
      });
      return;
    }

    viewport.scrollTo({
      top: viewport.scrollTop + slideBox.top - viewportBox.top,
      behavior: 'smooth'
    });
  }

  function enhance(bar) {
    if (bar.getAttribute('data-scrollbar-ready') === 'true') {
      if (bar._rebuildScrollbar) bar._rebuildScrollbar();
      return;
    }

    var viewport = viewportFor(bar);
    if (!viewport) return;

    bar.setAttribute('data-scrollbar-ready', 'true');

    var slides = [];
    var scheduled = false;
    var paintScheduled = false;

    function paintActive() {
      var horizontal = axisFor(bar) === 'horizontal';
      bar.setAttribute('data-scrollbar-axis', horizontal ? 'horizontal' : 'vertical');

      var dots = bar.children;
      if (!slides.length || !dots.length) return;

      var current = activeIndex(viewport, slides, horizontal);
      for (var i = 0; i < dots.length; i++) {
        var on = i === current;
        dots[i].classList.toggle('is-active', on);
        if (on) dots[i].setAttribute('aria-current', 'true');
        else dots[i].removeAttribute('aria-current');
      }
    }

    function rebuild() {
      slides = visibleSlides(bar, viewport);
      var previous = bar._scrollbarSlides || [];
      bar.setAttribute('data-scrollbar-axis', axisFor(bar));

      if (sameSlides(previous, slides) && bar.children.length === slides.length) {
        bar.hidden = slides.length <= 1;
        paintActive();
        return;
      }

      bar._scrollbarSlides = slides.slice();
      bar.innerHTML = '';
      bar.hidden = slides.length <= 1;

      slides.forEach(function (slide, index) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'slider-counter__link slider-counter__link--dots link';
        button.setAttribute('aria-label', 'Show media ' + (index + 1) + ' of ' + slides.length);
        button.innerHTML = '<span class="dot"></span>';

        button.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          scrollSlideIntoView(viewport, slide, axisFor(bar) === 'horizontal');
        });

        bar.appendChild(button);
      });

      paintActive();
    }

    function requestRebuild() {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        if (!bar.isConnected) return;
        rebuild();
      });
    }

    viewport.addEventListener(
      'scroll',
      function () {
        if (paintScheduled) return;
        paintScheduled = true;
        window.requestAnimationFrame(function () {
          paintScheduled = false;
          paintActive();
        });
      },
      { passive: true }
    );

    if (window.ResizeObserver) {
      new ResizeObserver(requestRebuild).observe(viewport);
    }

    new MutationObserver(requestRebuild).observe(viewport, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'hidden']
    });

    var media = window.matchMedia('(max-width: ' + (breakpointOf(bar) - 1) + 'px)');
    if (media.addEventListener) media.addEventListener('change', requestRebuild);
    else if (media.addListener) media.addListener(requestRebuild);

    bar._rebuildScrollbar = rebuild;
    bar._updateScrollbar = paintActive;

    var gallery = bar.closest('media-gallery') || bar.closest('[data-card-media-slider]');
    if (gallery) {
      gallery._rebuildMediaDots = rebuild;
      gallery._updateActiveMediaDot = paintActive;
      gallery.dataset.mediaDotsInitialized = 'true';
    }

    rebuild();
  }

  function boot() {
    var nodes = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < nodes.length; i++) enhance(nodes[i]);
  }

  function rebuildAll() {
    var nodes = document.querySelectorAll('[data-scrollbar-ready="true"]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i]._rebuildScrollbar) nodes[i]._rebuildScrollbar();
    }
  }

  /* custom.js calls these after a variant reload and on media-dots:refresh. */
  window.initMediaDots = function (options) {
    if (!options || !options.gallerySelector) {
      boot();
      return;
    }

    document.querySelectorAll(options.gallerySelector).forEach(function (gallery) {
      var dots = gallery.querySelector(options.dotsSelector);
      if (!dots) return;
      if (options.sliderSelector && !dots.getAttribute('data-scrollbar-viewport')) {
        dots.setAttribute('data-scrollbar-viewport', options.sliderSelector);
      }
      if (options.slideSelector && !dots.getAttribute('data-scrollbar-slide')) {
        dots.setAttribute('data-scrollbar-slide', options.slideSelector);
      }
      enhance(dots);
    });
  };

  window.initProductMediaDots = boot;
  window.initCardMediaDots = boot;
  window.initializeAllMediaDots = boot;
  window.rebuildAllMediaDots = rebuildAll;

  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('media-dots:refresh', function () {
    boot();
    rebuildAll();
  });

  /* Galleries inserted after load (variant HTML, load-more, new sections). */
  var scanScheduled = false;
  new MutationObserver(function () {
    if (scanScheduled) return;
    scanScheduled = true;
    window.requestAnimationFrame(function () {
      scanScheduled = false;
      boot();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();